import React from 'react';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import MuiSwitch from '../FormWidgets/SettingsSwitch';
import Desc from '../FormWidgets/Description';
import { ValueBox } from '../FormWidgets/SettingGroup';
import { supportsScheduledLightChannels } from '../Utils/light_profiles';
import { availableScheduleFillingTanks, scheduleFillingTanks } from '../Utils/scheduling_utils';
import { useTranslation } from 'react-i18next';
import { supportsCirculationSettings } from '../Utils/ventilation_utils';
import { DeepCopyObj, makePhase, makePhaseFromZoneSettings } from '../Utils/utils';

const SINGLE_SELECTION_IDS = [
  'schedule_humidity_enabled',
  'schedule_main_temperature_enabled',
  'schedule_vpd_enabled',
  'schedule_heating_mat_enabled',
  'schedule_co2_concentration_enabled',
  'schedule_co2_temperature_enabled',
  'schedule_day_night_enabled'
];

export function filterScheduleItemsByBitmap(items, bitmap, getBitIndex = (_, index) => index) {
  const selectedBitmap = Number(bitmap ?? 0);
  return items.filter((item, index) => {
    const bitIndex = getBitIndex(item, index);
    return (selectedBitmap & (1 << bitIndex)) !== 0;
  });
}

function indexedItems(count) {
  return Array.from({ length: count }, (_, bitIndex) => ({
    bitIndex,
    displayIndex: bitIndex + 1
  }));
}

export function getScheduleIndexedSelectionGroups(
  controller, parent, metadata = parent.settings.scheduling?.schedule_meta
) {
  const dynamicIntervals = parent.settings.timing?.interval_timer ?? [];
  const dynamicEvents = parent.settings.timing?.event_timer ?? [];
  const groups = [
    {
      masterId: 'schedule_lights_enabled',
      bitmapId: 'schedule_lights_bitmap',
      labelId: 'sub_zone',
      items: supportsScheduledLightChannels(controller, metadata?.protocol_version) ? [] : indexedItems(4)
    },
    {
      masterId: 'schedule_nutrients_enabled',
      bitmapId: 'schedule_nutrients_bitmap',
      labelId: 'sub_zone',
      items: indexedItems(4)
    },
    {
      masterId: 'schedule_irrigations_enabled',
      bitmapId: 'schedule_irrigations_bitmap',
      labelId: 'sub_zone',
      items: indexedItems(4)
    },
    {
      masterId: 'schedule_intervals_enabled',
      bitmapId: 'schedule_intervals_bitmap',
      items: indexedItems(controller.type === 6000 ? Math.min(dynamicIntervals.length, 2) : 2)
    },
    {
      masterId: 'schedule_events_enabled',
      bitmapId: 'schedule_events_bitmap',
      items: indexedItems(controller.type === 6000 ? Math.min(dynamicEvents.length, 8) : 8)
    }
  ];
  if (supportsCirculationSettings(controller, parent)) groups.push({
    masterId: 'schedule_circulation_enabled',
    bitmapId: 'schedule_circulation_bitmap',
    items: indexedItems(4)
  });
  const nutrientsIndex = groups.findIndex((group) => group.masterId === 'schedule_nutrients_enabled');
  const [nutrients] = groups.splice(nutrientsIndex, 1);
  groups.unshift(nutrients);
  const tanks = availableScheduleFillingTanks(parent);
  if (tanks.length || metadata.schedule_tank_filling_bitmap) groups.splice(1, 0, {
    masterId: 'schedule_tank_filling_enabled',
    bitmapId: 'schedule_tank_filling_bitmap',
    items: tanks.map(({ tank_id: id }) => ({
      bitIndex: id,
      displayIndex: id,
      label: parent.settings.irrigation_tank_info?.find((live) => live.tank_id === id)?.name
    }))
  });
  return groups;
}

function ScheduleMetaSwitch({ id, values, controls, parent }) {
  return (
    <Typography gutterBottom>
      <MuiSwitch
        checked={values.schedule_meta[id] === 1}
        controller_value={parent.settings.scheduling.schedule_meta[id]}
        onClick={(event) => controls.setValue(`schedule_meta.${id}`, event.target.checked ? 1 : 0)}
        color="primary"
        inputProps={{ 'aria-label': id }}
      />
      <Desc id={id} />
    </Typography>
  );
}

function ScheduleMetaBitmapCheckbox({ bitmapId, labelId = bitmapId, bitIndex, displayIndex, label, values, controls }) {
  const bitmap = Number(values.schedule_meta[bitmapId] ?? 0);
  const bit = 1 << bitIndex;
  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={(bitmap & bit) !== 0}
          onChange={(event) =>
            controls.setValue(
              `schedule_meta.${bitmapId}`,
              event.target.checked ? bitmap | bit : bitmap & ~bit
            )
          }
          color="primary"
          inputProps={{ 'aria-label': `${bitmapId}_${displayIndex}` }}
        />
      }
      label={
        <>
          {label || <><Desc id={labelId} /> {displayIndex}</>}
        </>
      }
    />
  );
}

export default function ScheduleSelection({ controller, parent, values, controls }) {
  const { t } = useTranslation();
  const channelLighting = supportsScheduledLightChannels(controller, values.schedule_meta.protocol_version);
  const singleSelections = channelLighting ? [...SINGLE_SELECTION_IDS, 'schedule_lights_enabled'] : SINGLE_SELECTION_IDS;
  const indexedGroups = getScheduleIndexedSelectionGroups(controller, parent, values.schedule_meta)
    .filter((group) => !(channelLighting && group.masterId === 'schedule_lights_enabled'));
  const availableTanks = availableScheduleFillingTanks(parent);
  const unavailableTanks = Array.from({ length: 8 }, (_, id) => id).filter((id) =>
    (values.schedule_meta.schedule_tank_filling_bitmap & (1 << id)) &&
    !availableTanks.some((tank) => tank.tank_id === id));
  const selectionControls = {
    ...controls,
    setValue(path, value) {
      const rowKey = {
        'schedule_meta.schedule_nutrients_bitmap': 'nutrient_settings',
        'schedule_meta.schedule_irrigations_bitmap': 'irrigation_settings'
      }[path];
      if (rowKey && values.schedule_values?.scheduling_values) {
        const ordinary = makePhaseFromZoneSettings(parent.settings, 0)[rowKey];
        const defaults = makePhase({})[rowKey];
        const selectedCount = Math.max(0, ...indexedItems(4)
          .filter(({ bitIndex }) => value & (1 << bitIndex))
          .map(({ bitIndex }) => bitIndex + 1));
        const phases = values.schedule_values.scheduling_values.map((phase) => {
          const rows = phase[rowKey] ?? [];
          const count = Math.max(rows.length, selectedCount);
          return {
            ...phase,
            [rowKey]: Array.from({ length: count }, (_, index) =>
              rows[index] ?? DeepCopyObj(ordinary[index] ?? defaults[index])),
            [`${rowKey}_count`]: count
          };
        });
        controls.setValue([path, 'schedule_values.scheduling_values'], [value, phases]);
        return;
      }
      if (path !== 'schedule_meta.schedule_tank_filling_bitmap') {
        controls.setValue(path, value);
        return;
      }
      const selectedTanks = scheduleFillingTanks(parent)
        .filter((tank) => value & (1 << tank.tank_id));
      const phases = values.schedule_values.scheduling_values.map((phase) => ({
        ...phase,
        tank_filling: [
          ...(phase.tank_filling ?? []),
          ...selectedTanks.filter((tank) => !phase.tank_filling?.some((row) => row.tank_id === tank.tank_id))
            .map((tank) => ({ ...tank }))
        ]
      }));
      controls.setValue([path, 'schedule_values.scheduling_values'], [value, phases]);
    }
  };
  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={6}>
        <ValueBox controller={controller} id="scheduling_">
          <ScheduleMetaSwitch
            id="schedule_enabled"
            values={values}
            controls={controls}
            parent={parent}
          />
        </ValueBox>
      </Grid>

      <Grid item xs={12} md={6}>
        <ValueBox controller={controller} id="scheduling_">
          {singleSelections.map((id) => (
            <ScheduleMetaSwitch
              key={id}
              id={id}
              values={values}
              controls={controls}
              parent={parent}
            />
          ))}
        </ValueBox>
      </Grid>

      {indexedGroups.map(({ masterId, bitmapId, labelId, items }) => (
        <Grid key={masterId} item xs={12} md={6}>
          <ValueBox controller={controller} id="scheduling_">
            <ScheduleMetaSwitch id={masterId} values={values} controls={controls} parent={parent} />
            <Box display="flex" flexWrap="wrap">
              {items.map(({ bitIndex, displayIndex, label }) => (
                <ScheduleMetaBitmapCheckbox
                  key={bitIndex}
                  bitmapId={bitmapId}
                  labelId={labelId}
                  bitIndex={bitIndex}
                  displayIndex={displayIndex}
                  label={label}
                  values={values}
                  controls={selectionControls}
                />
              ))}
            </Box>
            {masterId === 'schedule_tank_filling_enabled' && unavailableTanks.length > 0 && (
              <Typography variant="body2" color="textSecondary">
                {t('schedule_tanks_unavailable', { tanks: unavailableTanks.map((id) =>
                  parent.settings.irrigation_tank_info?.find((tank) => tank.tank_id === id)?.name ||
                  t('tank_id', { id })).join(', ') })}
              </Typography>
            )}
          </ValueBox>
        </Grid>
      ))}
    </Grid>
  );
}
